import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getLocation } from "@/data/locations";
import { api, whatsappLink } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";
import ClinicCard from "@/components/ClinicCard";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MapPin, Plane, Coins, Languages, Sun, Star, Check, ArrowLeft, Headset, MessageCircle, Compass, PlaneLanding } from "lucide-react";

export default function LocationDetail() {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const { t } = useLang();
  const loc = getLocation(slug);
  const [clinics, setClinics] = useState([]);

  useEffect(() => {
    if (!loc) return;
    api.get("/clinics").then((r) => setClinics(r.data.filter((c) => c.city === loc.city))).catch(() => {});
  }, [loc]);

  if (!loc) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-muted-foreground">{t("locations.notFound")} <Link to="/locations" className="text-primary underline">{t("locations.allDestinations")}</Link></div>;

  const FACTS = [
    { icon: Plane, label: t("locations.factGetting"), value: loc.facts.flight },
    { icon: PlaneLanding, label: t("locations.factEntry"), value: loc.facts.crossing },
    { icon: Coins, label: t("locations.factCurrency"), value: loc.facts.currency },
    { icon: Languages, label: t("locations.factLanguage"), value: loc.facts.language },
    { icon: Sun, label: t("locations.factWeather"), value: loc.facts.weather },
    { icon: Star, label: t("locations.factBestFor"), value: loc.facts.bestFor },
  ];

  return (
    <div>
      <section className="relative h-[380px] sm:h-[440px]">
        <img src={loc.hero} alt={`${loc.city}, ${loc.country}`} className="absolute inset-0 w-full h-full object-cover" data-testid="location-hero" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/50 to-secondary/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-end pb-10 text-white">
          <Link to="/locations" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mb-4 w-fit" data-testid="back-to-locations"><ArrowLeft className="w-4 h-4" /> {t("locations.allDestinations")}</Link>
          <p className="flex items-center gap-1.5 text-sm text-white/80"><MapPin className="w-4 h-4" /> {loc.country}</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-6xl mt-1">{loc.city}</h1>
          <p className="text-lg text-white/85 mt-3 max-w-xl">{loc.tagline}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          <div>
            <h2 className="font-heading text-2xl font-bold mb-4">{t("locations.about", { city: loc.city })}</h2>
            <div className="space-y-4 text-foreground/80 leading-relaxed">
              {loc.about.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {loc.whyChoose.map((w) => (
                <div key={w} className="flex items-start gap-2 rounded-xl border border-border bg-white p-3 text-sm">
                  <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {w}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4"><Compass className="w-6 h-6 text-primary" /><h2 className="font-heading text-2xl font-bold">{t("locations.whatToDo", { city: loc.city })}</h2></div>
            <div className="grid sm:grid-cols-2 gap-5">
              {loc.attractions.map((a) => (
                <Card key={a.title} className="p-5 rounded-2xl">
                  <h3 className="font-heading font-bold">{a.title}</h3>
                  <ul className="mt-2 space-y-1.5">
                    {a.items.map((it) => <li key={it} className="flex items-start gap-2 text-sm text-foreground/75"><span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" /> {it}</li>)}
                  </ul>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4"><Plane className="w-6 h-6 text-primary" /><h2 className="font-heading text-2xl font-bold">{t("locations.howToGet", { city: loc.city })}</h2></div>
            <Card className="p-6 rounded-2xl bg-primary/5 border-primary/10">
              <p className="text-foreground/80 leading-relaxed">{loc.getting}</p>
              <div className="flex items-center gap-2 mt-4 text-sm font-medium text-primary">
                <Headset className="w-4 h-4" /> {t("locations.coordinatorPickup")}
              </div>
            </Card>
          </div>

          <div>
            <h2 className="font-heading text-2xl font-bold mb-4">{clinics.length > 0 ? t("locations.clinicsIn", { city: loc.city }) : t("locations.clinicsNear", { city: loc.city })}</h2>
            {clinics.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="location-clinics">
                {clinics.map((c, i) => <ClinicCard key={c.id} clinic={c} index={i} />)}
              </div>
            ) : (
              <Card className="p-6 rounded-2xl text-sm text-muted-foreground">{t("locations.addingSoon", { city: loc.city })} <button className="text-primary underline" onClick={() => openQuote({ type: "quote" })}>{t("locations.requestQuote")}</button> {t("locations.addingSoon2")}</Card>
            )}
          </div>
        </div>

        <div className="lg:sticky lg:top-24 h-fit space-y-5">
          <Card className="p-6 rounded-2xl">
            <h3 className="font-heading font-bold mb-4">{t("locations.quickFacts")}</h3>
            <div className="space-y-4">
              {FACTS.map((f) => (
                <div key={f.label} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><f.icon className="w-4 h-4 text-primary" /></div>
                  <div>
                    <p className="text-xs text-muted-foreground">{f.label}</p>
                    <p className="text-sm font-medium">{f.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-6 rounded-2xl bg-secondary text-white">
            <h3 className="font-heading font-bold text-lg">{t("locations.planTrip", { city: loc.city })}</h3>
            <p className="text-sm text-white/70 mt-1">{t("locations.planSub")}</p>
            <div className="space-y-3 mt-4">
              <Button className="w-full rounded-full h-11 bg-accent hover:bg-accent/90" onClick={() => openQuote({ type: "quote" })} data-testid="location-quote-btn">{t("common.getFreeQuote")}</Button>
              <Button variant="outline" className="w-full rounded-full h-11 bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary" onClick={() => openQuote({ type: "call" })} data-testid="location-call-btn"><Headset className="w-4 h-4 mr-2" /> {t("common.talkToCoordinator")}</Button>
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="block">
                <Button variant="outline" className="w-full rounded-full h-11 bg-transparent border-white/40 text-white hover:bg-white hover:text-secondary" data-testid="location-whatsapp-btn"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</Button>
              </a>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
